import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import useFetch from "../../../hooks/useFetch";

const rankingFilters = [
  "ALL",
  "TOP_PERFORMERS",
  "HIGH_ATTENDANCE",
  "DISCIPLINED",
  "ACTIVE",
  "LOW_RISK",
];

const HAFHStudentRankings = ({ t, hostelCode, role }) => {
 const authState = useSelector((state) => state.authReducer || {});
   const navigate = useNavigate();
   const [fetchData] = useFetch();
 
   const isLoggedIn = authState?.isLoggedIn || false;
   const userId = authState?.user?._id || null;
   const userCode = authState?.user?.think5whyscode || "User";
   const userName = authState?.user?.name || "User";
 
  //  const [userRole, setUserRole] = useState(role);
 
  const [rankings, setRankings] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState("ALL");
  const [loading, setLoading] = useState(false);

  /* ================= FETCH ================= */

  const fetchRankings = async () => {
    try {
      setLoading(true);

      const res = await fetchData(
        {
          url: "/rankings/my",
          method: "post",
          data: {
            userId, moduleName: "hafh"
          },
          headers: {
            Authorization: authState.token,
          },
        },
        {
          showSuccessToast: false,
        }
      );

      if (res?.status) {
        setRankings(res.records || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

useEffect(() => {
  if (userId) {
    fetchRankings();
  }
}, [userId]);

  /* ================= FILTERING ================= */

  const filteredRankings = useMemo(() => {
    let data = [...rankings];

    switch (selectedFilter) {
      case "TOP_PERFORMERS":
        data = data.filter((r) => r.hybridScore >= 0.8);
        break;

      case "HIGH_ATTENDANCE":
        data = data.filter(
          (r) => (r.attendanceConsistency || 0) >= 0.8
        );
        break;

      case "DISCIPLINED":
        data = data.filter(
          (r) => (r.behaviorPenalty || 0) <= 0.2
        );
        break;

      case "ACTIVE":
        data = data.filter(
          (r) => (r.activityScore || 0) >= 0.5
        );
        break;

      case "LOW_RISK":
        data = data.filter(
          (r) => (r.riskScore || 0) <= 0.3
        );
        break;

      default:
        break;
    }

    return data.sort(
      (a, b) => (b.hybridScore || 0) - (a.hybridScore || 0)
    );
  }, [rankings, selectedFilter]);

  /* ================= HELPERS ================= */

  const getRankingBadge = (score = 0) => {
    if (score >= 0.9)
      return {
        label: "ELITE",
        color: "text-yellow-300",
      };

    if (score >= 0.75)
      return {
        label: "HIGH",
        color: "text-green-300",
      };

    if (score >= 0.5)
      return {
        label: "AVERAGE",
        color: "text-cyan-300",
      };

    return {
      label: "RISK",
      color: "text-red-300",
    };
  };

  /* ================= UI ================= */

  return (
    <div className="bg-gray-900 p-5 rounded-xl text-white space-y-4">

      {/* HEADER */}
      <div className="flex justify-between items-center">
        <h2 className="text-cyan-300 text-lg font-bold">
          🧠 Student Hybrid Rankings
        </h2>

        <button
          onClick={fetchRankings}
          className="w-24 lg:w-32 text-xs lg:text-sm px-3 py-2 bg-blue-600 rounded"
        >
          Refresh
        </button>
      </div>

      {/* FILTERS */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-1">

        {rankingFilters.map((f) => (
          <button
            key={f}
            onClick={() => setSelectedFilter(f)}
            className={`px-2 py-2 rounded-md text-xs transition-all ${
              selectedFilter === f
                ? "bg-teal-600"
                : "bg-gray-700"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {/* LOADING */}
      {loading && (
        <div className="text-sm text-gray-400">
          Loading rankings...
        </div>
      )}

      {/* LIST */}
      <div className="space-y-3">

        {!loading && filteredRankings.length === 0 && (
          <div className="text-gray-400 text-sm">
            No ranking data found
          </div>
        )}

        {filteredRankings.map((r, index) => {
          const badge = getRankingBadge(r.hybridScore);

          return (
            <div
              key={r._id || index}
              className="bg-gray-800 p-4 rounded-xl border border-gray-700 space-y-3"
            >

              {/* TOP */}
              <div className="flex justify-between items-start">

                <div>
                  <div className="text-cyan-300 font-semibold text-sm lg:text-base">
                    {r.userName || "Unknown Student"}
                  </div>
{/* 
                  <div className="text-xs text-gray-400">
                    {r.userCode || "No Code"}
                  </div> */}
                </div>

                <div className="text-right">
                  <div className="text-lg font-bold text-yellow-300">
                    #{index + 1}
                  </div>

                  <div className={`text-xs ${badge.color}`}>
                    {badge.label}
                  </div>
                </div>
              </div>

              {/* SCORE */}
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span>Hybrid Score</span>

                  <span className="text-cyan-300">
                    {Number(r.hybridScore || 0).toFixed(2)}
                  </span>
                </div>

                <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
                  <div
                    className="bg-teal-500 h-3"
                    style={{
                      width: `${(r.hybridScore || 0) * 100}%`,
                    }}
                  />
                </div>
              </div>

              {/* METRICS */}
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">

                <div className="bg-gray-700 rounded p-2">
                  <div className="text-[10px] text-gray-400">
                    Attendance
                  </div>

                  <div className="text-sm text-green-300">
                    {Number(
                      r.attendanceConsistency || 0
                    ).toFixed(2)}
                  </div>
                </div>

                <div className="bg-gray-700 rounded p-2">
                  <div className="text-[10px] text-gray-400">
                    Punctuality
                  </div>

                  <div className="text-sm text-cyan-300">
                    {Number(
                      r.punctualityScore || 0
                    ).toFixed(2)}
                  </div>
                </div>

                <div className="bg-gray-700 rounded p-2">
                  <div className="text-[10px] text-gray-400">
                    Activity
                  </div>

                  <div className="text-sm text-purple-300">
                    {Number(
                      r.activityScore || 0
                    ).toFixed(2)}
                  </div>
                </div>

                <div className="bg-gray-700 rounded p-2">
                  <div className="text-[10px] text-gray-400">
                    AI Boost
                  </div>

                  <div className="text-sm text-yellow-300">
                    {Number(
                      r.qdrantBoost || 0
                    ).toFixed(2)}
                  </div>
                </div>

                <div className="bg-gray-700 rounded p-2">
                  <div className="text-[10px] text-gray-400">
                    Risk
                  </div>

                  <div className="text-sm text-red-300">
                    {Number(
                      r.behaviorPenalty || 0
                    ).toFixed(2)}
                  </div>
                </div>
              </div>

              {/* SIGNALS */}
              {r.lastSignalType && (
                <div className="text-xs text-gray-400">
                  Last Cognitive Signal:
                  <span className="text-teal-300 ml-1">
                    {r.lastSignalType}
                  </span>
                </div>
              )}

              {/* ACTIONS */}
              <div className="flex gap-2 pt-1">

                <button className="px-3 py-1 bg-blue-600 text-xs rounded">
                  View
                </button>

                <button className="px-3 py-1 bg-purple-600 text-xs rounded">
                  Analytics
                </button>

                <button className="px-3 py-1 bg-green-600 text-xs rounded">
                  Share
                </button>

              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default HAFHStudentRankings;